/** The password rules External ID enforces, checked as the user types (ZEY-3140). The server has the last word. */
export interface PasswordCheck {
  length: boolean;
  mix: boolean;
  match: boolean;
  valid: boolean;
}

export const MIN_PASSWORD_LENGTH = 8;

/** How many of upper case, lower case, digits and symbols the password uses. */
export function characterClasses(password: string): number {
  const value = password ?? '';
  return [/[A-Z]/, /[a-z]/, /[0-9]/, /[^A-Za-z0-9]/].filter((re) => re.test(value)).length;
}

export function checkPassword(password: string, confirm: string): PasswordCheck {
  const length = (password ?? '').length >= MIN_PASSWORD_LENGTH;
  const mix = characterClasses(password) >= 3;
  const match = !!password && password === confirm;
  return { length, mix, match, valid: length && mix && match };
}

/** The first message of a ValidationException 400 (`{ errors: { newPassword: ["…"] } }`), if any. */
export function serverMessage(error: any): string | null {
  const errors = error?.error?.errors;
  if (!errors || typeof errors !== 'object') {
    return null;
  }
  for (const key of Object.keys(errors)) {
    const first = Array.isArray(errors[key]) ? errors[key][0] : errors[key];
    if (typeof first === 'string' && first.trim()) {
      return first;
    }
  }
  return null;
}

/** The typed failure the API sends with a 400 from SetPassword (ZEY-3140). */
export type PasswordFailureCode = 'PasswordRejected' | 'NotAvailable' | 'B2CFailed' | 'TooManyAttempts' | 'Failed';

export interface PasswordFailure {
  code: PasswordFailureCode;
  /** For PasswordRejected: `length`, `complexity` or `common`. */
  rule: string | null;
  /** The server's own text, used when the code is unknown. */
  message: string | null;
}

const KNOWN_CODES: PasswordFailureCode[] = ['PasswordRejected', 'NotAvailable', 'B2CFailed', 'TooManyAttempts', 'Failed'];

/** Reads `code` / `rule` from the API's 400 (ValidationProblemDetails extensions); anything else is a plain failure. */
export function passwordFailure(error: any): PasswordFailure {
  const body = error?.status === 400 ? error?.error : null;
  const code = KNOWN_CODES.find((c) => c === body?.code) ?? 'Failed';
  const rule = typeof body?.rule === 'string' ? body.rule : null;
  return { code, rule, message: body ? serverMessage(error) : null };
}

/** Translation key of the message to show for a failure. */
export function failureKey(failure: PasswordFailure): string {
  switch (failure.code) {
    case 'PasswordRejected':
      return failure.rule === 'length'
        ? 'passwordPromptErrLength'
        : failure.rule === 'complexity'
          ? 'passwordPromptErrComplexity'
          : 'passwordPromptErrCommon';
    case 'NotAvailable':
      return 'passwordPromptErrNotAvailable';
    case 'B2CFailed':
      return 'passwordPromptErrB2C';
    case 'TooManyAttempts':
      return 'passwordPromptErrTooMany';
    default:
      return 'passwordPromptFailed';
  }
}
